import { useState, useEffect } from 'react';
import styles from './DraftOrganoidDetailInfoComponent.module.css';

const FIELDS = [
    { key: 'Key',                  label: 'GBCC Code' },
    { key: 'Type',                 label: 'Disease Type' },
    { key: 'Subtype',              label: 'Subtype' },
    { key: 'sampleSource',         label: 'Sample Source' },
    { key: 'SampleType',           label: 'Sample Type' },
    { key: 'site',                 label: 'Site' },
    { key: 'cro_status',           label: 'CRO Stat' },   // ★ [추가] CRO Stat
    { key: 'surgeryDate',          label: 'Surgery Date' },
    { key: 'Pair',                 label: 'N/T Pair' },
    { key: 'immuneCellPair',       label: 'Immune Cell Pair' },
    { key: 'CAFPair',              label: 'CAF Pair' },
    { key: 'WES',                  label: 'WES' },
    { key: 'RNASeq',               label: 'RNA Seq.' },
    { key: 'MutationInformation',  label: 'Mutation Information' },
    { key: 'MSS',                  label: 'PDO Annotation' },
];

const hashmarkToSharp = (str) => {
    if (!str) return '';
    return String(str).replace(/hashmark/g, '#');
};

// ★ [추가] 비교/표시용 정규화:
//  - 리터럴 "\n"(역슬래시+n)을 실제 개행으로 통일
//  - CRLF/CR -> LF 통일
//  - 양끝 공백 제거
// 이렇게 해야 "내용은 같은데 개행 표기만 다른" 경우에 변경됨으로 오탐하지 않음.
const normalizeForCompare = (val) => {
    if (val === null || val === undefined) return '';
    return String(val)
        .replace(/\\n/g, '\n')   // 리터럴 \n -> 실제 개행
        .replace(/\r\n/g, '\n')  // CRLF -> LF
        .replace(/\r/g, '\n')    // CR -> LF
        .trim();
};

// SQL 값에서 불필요한 따옴표, 함수(COALESCE 등) 제거 로직
// ★ [수정] COALESCE(NULL, `col`) 처럼 첫 인자가 NULL이면 "변경 없음"을 의미하므로
//          특별값 null 을 돌려주어 호출부에서 원본 유지로 처리하게 함.
const cleanSqlValue = (val) => {
    let cleaned = val.trim();
    if (/^COALESCE\s*\(/i.test(cleaned)) {
        const inner = cleaned.match(/\((.*)\)/);
        if (inner) cleaned = inner[1].split(',')[0].trim();
    }
    // 따옴표/백틱 제거
    const unquoted = cleaned.replace(/^['`]+|['`]+$/g, '').trim();
    // ★ [추가] 순수 NULL 값은 "값 없음(원본 유지)"으로 간주
    if (/^null$/i.test(unquoted)) return null;
    return unquoted;
};

// 모든 상황에 대응하는 SQL 파서
const parseQuery = (query = '') => {
    const updates = {};
    if (!query) return updates;

    const normalized = query.replace(/\s+/g, ' ').trim();

    // 1. UPDATE 문 처리 (COALESCE 등 함수 및 괄호 대응)
    const setMatch = normalized.match(/SET\s+(.+?)\s+WHERE/i);
    if (setMatch) {
        const content = setMatch[1];
        let currentPair = '', inQuotes = false, parenCount = 0;

        const commit = (pair) => {
            const [k, ...v] = pair.split('=');
            if (!k) return;
            const key = k.trim().replace(/[`'"]/g, '');
            const value = cleanSqlValue(v.join('='));
            // ★ [수정] null(=COALESCE 첫 인자 NULL 등)이면 업데이트 대상에서 제외 -> 원본 유지
            if (value !== null) updates[key] = value;
        };

        for (let i = 0; i < content.length; i++) {
            const ch = content[i];
            if (ch === "'") inQuotes = !inQuotes;
            if (!inQuotes) {
                if (ch === '(') parenCount++;
                if (ch === ')') parenCount--;
            }

            if (ch === ',' && !inQuotes && parenCount === 0) {
                commit(currentPair);
                currentPair = '';
            } else {
                currentPair += ch;
            }
        }
        if (currentPair) commit(currentPair);
    }
    // 2. INSERT 문 처리
    else {
        const insertRe = /INSERT\s+INTO\s+[^()]+\s*\(([^)]+)\)\s*VALUES\s*\(([\s\S]+)\)/i;
        const match = normalized.match(insertRe);
        if (match) {
            const keys = match[1].split(',').map(s => s.trim().replace(/[`'"]/g, ''));
            const valuesRaw = match[2].trim();
            const values = [];
            let cur = '', inQuotes = false, parenCount = 0;

            for (let i = 0; i < valuesRaw.length; i++) {
                const ch = valuesRaw[i];
                if (ch === "'") inQuotes = !inQuotes;
                if (!inQuotes) {
                    if (ch === '(') parenCount++;
                    if (ch === ')') parenCount--;
                }
                if (ch === ',' && !inQuotes && parenCount === 0) {
                    values.push(cleanSqlValue(cur));
                    cur = '';
                } else {
                    cur += ch;
                }
            }
            if (cur) values.push(cleanSqlValue(cur));
            keys.forEach((key, i) => {
                const value = values[i];
                // ★ [수정] null이면 빈 문자열로(INSERT는 신규라 원본이 없음)
                updates[key] = value === null || value === undefined ? '' : value;
            });
        }
    }
    return updates;
};

function DraftOrganoidDetailInfoComponent({ selectedDraft, targetKey }) {
    const [organoidData, setOrganoidData] = useState({});
    const [draftUpdates, setDraftUpdates] = useState({});

    useEffect(() => {
        if (targetKey) {
            fetch('/api/getOrganoidInfo_noLog', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ param: targetKey }),
            })
                .then(res => res.json())
                .then(data => {
                    if (data && data.length) setOrganoidData(data[0]);
                    else setOrganoidData({});
                })
                .catch(() => setOrganoidData({}));
        }

        const tempQuery = selectedDraft.tempQuery || '';
        setDraftUpdates(parseQuery(tempQuery));
    }, [targetKey, selectedDraft]);

    const renderField = ({ key, label }) => {
        const orig = organoidData[key] ?? '';
        // ★ [수정] draftUpdates에 해당 key가 없으면(=원본 유지) 원본값을 그대로 사용
        const hasUpd = Object.prototype.hasOwnProperty.call(draftUpdates, key);
        const upd = hasUpd ? (draftUpdates[key] ?? '') : orig;

        const displayOrig = hashmarkToSharp(orig);
        const displayUpd = hashmarkToSharp(upd);

        // ★ [수정] 개행 표기 차이를 정규화한 뒤 비교 -> 내용 동일 시 화살표 미표시
        const normOrig = normalizeForCompare(orig);
        const normUpd = normalizeForCompare(upd);

        const isChanged = normOrig !== '' && normUpd !== '' && normOrig !== normUpd;
        const isNew = (normOrig === '' || orig === null || orig === 'N/A') && normUpd !== '';

        return (
            <div className={styles.fieldRow} key={key}>
                <div className={styles.fieldLabel}>{label}</div>
                <div className={styles.fieldValue}>
                    {isChanged ? (
                        <>
                            <span className={styles.originalValue}>{displayOrig || 'N/A'}</span> →{' '}
                            <span className={styles.updatedValue}>{displayUpd || 'N/A'}</span>
                        </>
                    ) : isNew ? (
                        <span className={styles.updatedValue}>{displayUpd}</span>
                    ) : (
                        displayOrig || 'N/A'
                    )}
                </div>
            </div>
        );
    };

    const isNewRegistration = Object.keys(organoidData).length === 0 && Object.keys(draftUpdates).length > 0;

    return (
        <div className={styles.main}>
            <div className={styles.draftTitle}>
                Organoid Information
                {isNewRegistration && (
                    <span className={styles.newBadge} style={{ color: '#f39c12', marginLeft: '10px', fontSize: '0.8em' }}>
                        [New Registration]
                    </span>
                )}
            </div>
            {FIELDS.map(renderField)}
        </div>
    );
}

export default DraftOrganoidDetailInfoComponent;